import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import { Box, Divider, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { PanelHeader } from "../../../components/UIToolkit/molecules/PanelHeader";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { STATUS_BADGE } from "../../../theme/tokens";
import type { PbjLockedAction, PbjPrediction, PbjSuggestedPlan } from "../../../utils/models";
import { usePbjSuggestedPlan } from "../hooks/usePbjPrediction";
import { cardShadow, PBJ_ALPHA } from "./colors";
import type { PbjDraftControls } from "./pbjDraftControls";
import { RecommendationRow } from "./RecommendationRow";
import { buildPlanItem, calculateRecData } from "./recMath.utils";
import { type PlanItem, RECOMMENDATION_TYPES, type RecommendationType, recColor } from "./recommendations.constants";
import type { TrackerButtonState } from "./trackedPlan.utils";
import { WageOverridesDialog } from "./WageOverridesDialog";

// "qtd" = catch-up over remaining-from-today days; "quarter" = sustained across the
// full calendar quarter. Lifted to the parent so ActionItems uses the same mode.
export type PeriodMode = "qtd" | "quarter";

type Props = {
	prediction: PbjPrediction;
	census: number;
	// Live config draft + persist/recompute callbacks (one grouped prop shared with the
	// hero + breakdown table). The wage-overrides dialog seeds from `draft` and, on Save,
	// both writes the draft and recalculates with one click (like the census +
	// manual-turnover dialogs).
	draftControls: PbjDraftControls;
	// Full plan items (not just ids) so sliders + impact/points labels can reflect
	// the BE-suggested values verbatim when an action is in the plan.
	selectedPlan: PlanItem[];
	onAddToPlan: (item: PlanItem) => void;
	onUpdatePlan: (item: PlanItem) => void;
	// Reset the Action Plan back to the pre-computed suggestion (the post-refresh default).
	// Paired with re-seeding the sliders locally so "Reset plan" restores the whole section.
	onResetPlan: () => void;
	// Disable the button when the greedy returned no steps (already at/above target).
	canSuggest: boolean;
	periodMode: PeriodMode;
	onPeriodModeChange: (next: PeriodMode) => void;
	// Committed ISO date range — the window the suggested-plan endpoint re-optimizes
	// over when there are locked (manually-added) buckets in the plan.
	startDate: string;
	endDate: string;
	// "Add to tracker" — the whole assembled Action Plan, not one recommendation. The payload is
	// built in ScoringAndRecommendations (which owns selectedPlan + the resolved target); this
	// panel only renders the state and reports the click.
	trackerState: TrackerButtonState;
	onTrackPlan: () => void;
	isTrackingPlan: boolean;
};

export const RecommendationsSidebar: FC<Props> = ({
	prediction,
	census,
	draftControls,
	selectedPlan,
	onAddToPlan,
	onUpdatePlan,
	onResetPlan,
	canSuggest,
	periodMode,
	onPeriodModeChange,
	startDate,
	endDate,
	trackerState,
	onTrackPlan,
	isTrackingPlan,
}) => {
	const { draft, onDraftChange, onCalculate } = draftControls;
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { showErrorSnackbar } = useHospitalData();
	const theme = useTheme();
	const accent = theme.palette.primary.main;
	const brandTintC = alpha(accent, PBJ_ALPHA.TINT);
	const brandBorderC = alpha(accent, PBJ_ALPHA.BORDER);
	const suggestMutation = usePbjSuggestedPlan();

	// Wage-overrides dialog open state. The dialog (WageOverridesDialog) owns its own
	// per-field draft, validation, and save (draft + recompute in one click).
	const [wageOverridesOpen, setWageOverridesOpen] = useState(false);

	// In "Quarter to date" mode the math tooltips also reflect catch-up math:
	// you have `daysRemaining` of `quarterDays` left, and the per-remaining-day
	// rate to hit Q-end target is `sustained × quarterDays / daysRemaining`.
	const periodDays = periodMode === "quarter" ? prediction.quarterDays : prediction.days;
	const periodWeekendDays = periodMode === "quarter" ? prediction.quarterWeekendDays : prediction.weekendDays;
	// Hide slabs whose wage came back null from the BE (no active hourly nurses of
	// that role). The slider math (`HPPD = spend / wage`) needs a real wage, and the
	// product decision is to omit rather than fall back to a fake number.
	const visibleRecs = RECOMMENDATION_TYPES.filter((r) => prediction.deptWages[r.wageKey] !== null);
	// Index selectedPlan by recommendation id once per render so the per-rec lookup
	// below is O(1). Also lets us reflect the BE-suggested spend / impact / points
	// on the slider + labels without recomputing via FE math.
	const selectedById = useMemo(() => Object.fromEntries(selectedPlan.map((p) => [p.id, p])), [selectedPlan]);

	// Seed each visible rec's slider from a suggested plan: a rec mentioned by a step
	// takes that step's spend; everything else sits at 0. This positions the sliders
	// to reflect the (pre-computed or re-optimized) suggestion without touching the
	// Action Plan. Recs already in the plan render from their plan item's spend, so we
	// don't special-case them here.
	const spendMapFromPlan = useCallback(
		(plan: PbjSuggestedPlan): Record<string, number> => {
			const stepByAction = new Map(plan.steps.map((s) => [s.actionId, s]));
			return Object.fromEntries(visibleRecs.map((r) => [r.id, stepByAction.get(r.id)?.spend ?? 0]));
		},
		[visibleRecs],
	);

	// The visible recs a plan positions with a non-zero spend — seeds the "Suggested"
	// highlight set so a pre-positioned slider reads as suggested on load too (not only
	// after an explicit Suggest click).
	const suggestedIdsFromPlan = useCallback(
		(plan: PbjSuggestedPlan): Set<string> => {
			const visibleIds = new Set<string>(visibleRecs.map((r) => r.id));
			return new Set(plan.steps.filter((s) => s.spend > 0 && visibleIds.has(s.actionId)).map((s) => s.actionId));
		},
		[visibleRecs],
	);

	// Sliders are pre-positioned on load from the prediction's pre-computed suggested
	// plan (no network — an empty lockedActions request reproduces it exactly).
	const [spendMap, setSpendMap] = useState<Record<string, number>>(() => spendMapFromPlan(prediction.suggestedPlan));

	// Rec ids the current suggestion positioned with a non-zero spend — drives the
	// "Suggested" badge/tint + the one-shot slider land animation. Seeded on load so
	// the pre-positioned recs are highlighted immediately.
	const [suggestedRecIds, setSuggestedRecIds] = useState<Set<string>>(() => suggestedIdsFromPlan(prediction.suggestedPlan));

	// Re-seed sliders AND the suggested-highlight set whenever a fresh prediction arrives
	// (Calculate / quarter / target change all produce a new `prediction` object ref).
	// Depending on the `prediction` reference — not derived values — keeps this from
	// clobbering user-dragged sliders on unrelated re-renders.
	// biome-ignore lint/correctness/useExhaustiveDependencies: re-seed only when a new prediction object arrives, not when the seeding helper identities change.
	useEffect(() => {
		setSpendMap(spendMapFromPlan(prediction.suggestedPlan));
		setSuggestedRecIds(suggestedIdsFromPlan(prediction.suggestedPlan));
	}, [prediction]);

	// "Suggest plan" now ONLY moves sliders — it never mutates the Action Plan. When the
	// plan has manually-added buckets we send them as lockedActions so the BE re-optimizes
	// the OTHER buckets around them (treating the locked ones as already applied); with an
	// empty plan we reuse the prediction's pre-computed plan (no round-trip). Either way we
	// apply the resulting steps only to NON-locked recs' sliders and leave selectedPlan and
	// the locked recs' sliders untouched.
	const handleSuggestClick = useCallback(async () => {
		const lockedActions: PbjLockedAction[] = selectedPlan.map((p) => ({
			actionId: p.id,
			categoryGaps: Object.fromEntries((p.breakdown ?? []).map((b) => [b.categoryId, b.hppdGap])),
		}));
		track("pbj_model_suggest_plan_clicked", {
			department_id: prediction.config.departmentId,
			target_stars: prediction.targetStars,
			steps_count: prediction.suggestedPlan.steps.length,
			target_met: prediction.suggestedPlan.targetMet,
			locked_count: lockedActions.length,
		});
		// `mutateAsync` rejects on any 4xx/5xx. Without this catch the rejection is unhandled
		// and the click silently does nothing — the BE rejects an unrecognized locked action id
		// with a translated `detail.message` (pbj_model.invalid.locked_action), which the user
		// would never see. Sliders are deliberately left untouched on failure: a partial
		// re-position built from a plan we never received would misrepresent the suggestion.
		let plan: PbjSuggestedPlan;
		try {
			plan =
				lockedActions.length === 0
					? prediction.suggestedPlan
					: await suggestMutation.mutateAsync({ departmentId: prediction.config.departmentId, startDate, endDate, lockedActions });
		} catch (error) {
			const messageKey = (error as { response?: { data?: { detail?: { message?: string } } } })?.response?.data?.detail?.message;
			showErrorSnackbar(messageKey ? t(messageKey) : t("pbj_model.suggest_plan.failed"), { action: "pbj_model_suggest_plan" }, error);
			return;
		}
		const stepByAction = new Map(plan.steps.map((s) => [s.actionId, s]));
		const lockedIds = new Set(lockedActions.map((l) => l.actionId));
		const positioned = new Set<string>();
		setSpendMap((prev) => {
			const next = { ...prev };
			for (const rec of visibleRecs) {
				// Locked recs render from their plan item's spend — never touch their slider.
				if (lockedIds.has(rec.id)) continue;
				const spend = stepByAction.get(rec.id)?.spend ?? 0;
				next[rec.id] = spend;
				if (spend > 0) positioned.add(rec.id);
			}
			return next;
		});
		setSuggestedRecIds(positioned);
	}, [
		endDate,
		prediction.config.departmentId,
		prediction.suggestedPlan,
		prediction.targetStars,
		selectedPlan,
		showErrorSnackbar,
		startDate,
		suggestMutation,
		t,
		track,
		visibleRecs,
	]);

	const handleSliderChange = useCallback(
		(rec: RecommendationType, newSpend: number) => {
			setSpendMap((prev) => ({ ...prev, [rec.id]: newSpend }));
			// A manual drag means the user is no longer looking at the suggested value —
			// drop the "Suggested" affordance (badge/animation) for this rec.
			setSuggestedRecIds((prev) => {
				if (!prev.has(rec.id)) return prev;
				const next = new Set(prev);
				next.delete(rec.id);
				return next;
			});
			if (selectedById[rec.id]) {
				const wage = prediction.deptWages[rec.wageKey] ?? 0;
				const data = calculateRecData(rec, newSpend, wage, prediction, census);
				// Slider drag recomputes the per-category breakdown for the new spend; the
				// per-category `hppdGap` is baseline-independent (hours/census/days/scale
				// only), so storing it now is correct and order-independent. ActionItems'
				// cumulative pass re-scores the marginal points from this gap.
				onUpdatePlan(buildPlanItem(rec, newSpend, data));
			}
		},
		[census, onUpdatePlan, prediction, selectedById],
	);

	// The live spend each rec's slider currently shows: a rec already in the plan renders
	// from its plan item's spend, otherwise from the locally-tracked spendMap. Centralized
	// here so "Add all" pushes exactly what the user sees.
	const currentSpendFor = useCallback((recId: string): number => selectedById[recId]?.spend ?? spendMap[recId] ?? 0, [selectedById, spendMap]);

	// Every visible rec whose slider currently carries a non-zero value — the live slider
	// positions, whether suggested, pre-seeded on load, or hand-tuned, and INCLUDING ones
	// already in the plan. This is the "Add all" target set + button count, so the button
	// stays clickable + reflects the real number of valued sliders even when the default
	// suggestion is already populated.
	const valuedRecs = useMemo(() => visibleRecs.filter((r) => currentSpendFor(r.id) > 0), [visibleRecs, currentSpendFor]);

	// "Add all" pushes the CURRENT slider positions into the Action Plan in one click,
	// building each PlanItem from the same FE math the per-rec "Add" button uses so the
	// rows match. Recs already in the plan are re-synced to their current slider value
	// (update); the rest are added — so the plan always ends up matching every valued slider.
	const handleAddAll = useCallback(() => {
		track("pbj_model_add_all_clicked", { department_id: prediction.config.departmentId, count: valuedRecs.length });
		for (const rec of valuedRecs) {
			const wage = prediction.deptWages[rec.wageKey] ?? 0;
			const spend = currentSpendFor(rec.id);
			const item = buildPlanItem(rec, spend, calculateRecData(rec, spend, wage, prediction, census));
			if (selectedById[rec.id]) onUpdatePlan(item);
			else onAddToPlan(item);
		}
	}, [census, currentSpendFor, onAddToPlan, onUpdatePlan, selectedById, valuedRecs, prediction, track]);

	// "Reset plan" restores the post-refresh default: re-seed the sliders + the suggested
	// highlight from the pre-computed plan, and reset the parent's Action Plan the same way.
	// Equivalent to reloading the page (which re-seeds both from prediction.suggestedPlan).
	const handleReset = useCallback(() => {
		track("pbj_model_reset_plan_clicked", { department_id: prediction.config.departmentId });
		setSpendMap(spendMapFromPlan(prediction.suggestedPlan));
		setSuggestedRecIds(suggestedIdsFromPlan(prediction.suggestedPlan));
		onResetPlan();
	}, [onResetPlan, prediction.config.departmentId, prediction.suggestedPlan, spendMapFromPlan, suggestedIdsFromPlan, track]);

	const iconAffordanceSx = {
		width: 30,
		height: 30,
		borderRadius: 1.5,
		color: accent,
		bgcolor: brandTintC,
		border: "1px solid",
		borderColor: brandBorderC,
		"&:hover": { bgcolor: alpha(accent, PBJ_ALPHA.TINT + 0.06) },
	} as const;

	return (
		<Paper
			elevation={0}
			sx={{
				borderRadius: 3,
				overflow: "hidden",
				boxShadow: cardShadow(theme),
				height: "100%",
				display: "flex",
				flexDirection: "column",
			}}
			data-testid="pbj-model-recommendations"
		>
			<Box
				sx={{
					py: 1.25,
					px: 2,
					minHeight: 52,
					bgcolor: "background.default",
					borderBottom: "1px solid",
					borderColor: "divider",
					display: "flex",
					alignItems: "center",
				}}
			>
				{/* Titled panel header (accent tile + subtitle title). The settings cluster
				    (period toggle + wage gear) divided from the primary "Suggest Plan" action —
				    so configuration and the CTA read distinctly — rides the independent
				    `endingContent` slot, which right-aligns and wraps below the title on a narrow
				    panel (keeping the title from truncating). */}
				<PanelHeader
					icon={<TipsAndUpdatesOutlinedIcon />}
					title={t("pbj_model.recommendations.title")}
					endingContent={
						<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
							{/* Period toggle: switches the math tooltip's day denominator between
					    elapsed-to-today and full calendar-quarter. Only meaningful for
					    in-progress quarters — for completed ones the two values match. */}
							<SystemFlatSwitch<PeriodMode>
								options={[
									{ label: t("pbj_model.recommendations.period.qtd"), value: "qtd" },
									{ label: t("pbj_model.recommendations.period.quarter"), value: "quarter" },
								]}
								activeIndexOrValue={periodMode}
								onSwitch={(option) => onPeriodModeChange(option.value)}
								delayCallback={0}
								data-testid="pbj-model-recommendations-period-toggle"
							/>
							{/* Gear-wheel to manually edit the 6 assumed wages — a compact settings
					    affordance grouped with the period toggle, in the brand accent tokens. */}
							<Tooltip title={t("pbj_model.recommendations.wage_overrides.tooltip")} arrow>
								<IconButton size="small" onClick={() => setWageOverridesOpen(true)} sx={iconAffordanceSx} data-testid="pbj-model-wage-overrides-chip">
									<SettingsIcon sx={{ fontSize: 15 }} />
								</IconButton>
							</Tooltip>
							<Divider orientation="vertical" flexItem sx={{ my: 0.75 }} />
							<Tooltip title={canSuggest ? "" : t("pbj_model.recommendations.suggest_plan_unavailable")} arrow>
								{/* span wrapper so the tooltip still fires while the button is disabled */}
								<span>
									<SystemButton
										buttonVariant="primary"
										size="small"
										disabled={!canSuggest || suggestMutation.isPending}
										loading={suggestMutation.isPending}
										onClick={handleSuggestClick}
										leadingIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
										noFullWidthMobile
										data-testid="pbj-model-suggest-plan"
									>
										{t("pbj_model.recommendations.suggest_plan")}
									</SystemButton>
								</span>
							</Tooltip>
							{/* Reset back to the post-refresh default (sliders + Action Plan re-seeded from
					    the pre-computed suggestion). Secondary affordance beside the primary CTA. */}
							<Tooltip title={t("pbj_model.recommendations.reset_plan")} arrow>
								<IconButton size="small" onClick={handleReset} sx={iconAffordanceSx} data-testid="pbj-model-recommendations-reset">
									<RestartAltIcon sx={{ fontSize: 15 }} />
								</IconButton>
							</Tooltip>
						</Stack>
					}
				/>
			</Box>
			{visibleRecs.length === 0 ? (
				<Box sx={{ p: 3 }}>
					<Typography variant="caption" color="text.secondary">
						{t("pbj_model.recommendations.no_wage_data")}
					</Typography>
				</Box>
			) : (
				<Stack divider={<Divider />} sx={{ flexGrow: 1 }}>
					{visibleRecs.map((rec) => {
						const wage = prediction.deptWages[rec.wageKey] ?? 0;
						// Theme-derived accent for this lever (was a hard-coded hex on `rec.color`).
						const color = recColor(theme, rec.colorKey);
						const selectedItem = selectedById[rec.id];
						// The rec's live slider spend: the plan item's value when selected, otherwise
						// the locally-tracked spendMap value. `spendMap` only holds entries the plan
						// positioned, so a never-touched rec is absent → 0 (mirrors `currentSpendFor`).
						const currentSpend = selectedItem?.spend ?? spendMap[rec.id] ?? 0;
						const isSelected = !!selectedItem;
						// A rec the latest Suggest positioned (spend > 0) and not yet added — drives the
						// faint wash + accent rail, the "Suggested" badge, and the slider land-pulse.
						const isSuggested = suggestedRecIds.has(rec.id) && !isSelected && currentSpend > 0;
						// Inert = no spend dialed in and not in the plan (muted rail + track).
						const isInert = !isSelected && currentSpend <= 0;
						return (
							<RecommendationRow
								key={rec.id}
								rec={rec}
								wage={wage}
								color={color}
								selectedItem={selectedItem}
								currentSpend={currentSpend}
								isSuggested={isSuggested}
								isInert={isInert}
								prediction={prediction}
								census={census}
								selectedPlan={selectedPlan}
								periodMode={periodMode}
								periodDays={periodDays}
								periodWeekendDays={periodWeekendDays}
								draft={draft}
								onAdd={onAddToPlan}
								onSlider={handleSliderChange}
							/>
						);
					})}
				</Stack>
			)}

			{/* Footer: commit the current slider positions to the Action Plan in one click, and
			    beside it commit the WHOLE assembled plan to the tracker. Disabled when no slider
			    carries a value. (Reset plan lives in the header beside Suggest plan.) */}
			{visibleRecs.length > 0 && (
				<Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", bgcolor: "background.default" }}>
					<SystemButton
						buttonVariant="primary"
						size="small"
						disabled={valuedRecs.length === 0}
						onClick={handleAddAll}
						leadingIcon={<PlaylistAddCheckIcon sx={{ fontSize: 16 }} />}
						sx={{ width: "100%" }}
						data-testid="pbj-model-recommendations-add-all"
					>
						{t("pbj_model.recommendations.add_all", { count: valuedRecs.length })}
					</SystemButton>
					{/* Three states. "Tracked" is a settled confirmation, not a re-pressable button — it
					    renders disabled with a check so the affordance matches the meaning. */}
					<Tooltip arrow title={selectedPlan.length === 0 ? t("pbj_model.tracker.empty_plan_tooltip") : ""}>
						<Box sx={{ width: "100%" }}>
							<SystemButton
								buttonVariant={trackerState === "tracked" ? "secondary" : "primary"}
								size="small"
								disabled={selectedPlan.length === 0 || trackerState === "tracked" || isTrackingPlan}
								loading={isTrackingPlan}
								onClick={onTrackPlan}
								leadingIcon={
									trackerState === "tracked" ? (
										<CheckCircleIcon sx={{ fontSize: 16 }} />
									) : trackerState === "switch" ? (
										<SwapHorizIcon sx={{ fontSize: 16 }} />
									) : (
										<TrackChangesIcon sx={{ fontSize: 16 }} />
									)
								}
								sx={{
									width: "100%",
									// The settled state reads as a success confirmation, so it keeps its ink while
									// disabled — otherwise "Tracked" greys out and looks like it failed.
									...(trackerState === "tracked"
										? {
												color: STATUS_BADGE.SUCCESS_INK,
												borderColor: STATUS_BADGE.SUCCESS_INK,
												"&.Mui-disabled": { color: STATUS_BADGE.SUCCESS_INK, borderColor: STATUS_BADGE.SUCCESS_BG },
											}
										: {}),
								}}
								data-testid="pbj-model-recommendations-track-plan"
								data-tracker-state={trackerState}
							>
								{t(
									trackerState === "tracked"
										? "pbj_model.tracker.state_tracked"
										: trackerState === "switch"
											? "pbj_model.tracker.state_switch"
											: "pbj_model.tracker.state_add",
								)}
							</SystemButton>
						</Box>
					</Tooltip>
				</Stack>
			)}

			<WageOverridesDialog
				open={wageOverridesOpen}
				onClose={() => setWageOverridesOpen(false)}
				prediction={prediction}
				draft={draft}
				onDraftChange={onDraftChange}
				onCalculate={onCalculate}
			/>
		</Paper>
	);
};
